/**
 * eBay Sell API Service
 *
 * Posts listings through eBay's official Sell APIs using the Inventory model:
 * an inventory item is created for the SKU, an offer is attached to it, and
 * publishing the offer produces the live listing.
 *
 * OAuth credentials live in localStorage. Requests go through a worker proxy
 * (workers/src/routes/ebay.js) because eBay's APIs send no CORS headers.
 */

import { ebayAccount } from "./api";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8787";
const PROXY_URL = `${API_URL}/api/ebay`;

// Storage keys
const STORAGE_KEYS = {
  CLIENT_ID: "ebay_client_id",
  CLIENT_SECRET: "ebay_client_secret",
  REFRESH_TOKEN: "ebay_refresh_token",
  ACCESS_TOKEN: "ebay_access_token",
  TOKEN_EXPIRES_AT: "ebay_token_expires_at",
  ENVIRONMENT: "ebay_environment",
  MARKETPLACE_ID: "ebay_marketplace_id",
  MERCHANT_LOCATION_KEY: "ebay_merchant_location_key",
  FULFILLMENT_POLICY_ID: "ebay_fulfillment_policy_id",
  PAYMENT_POLICY_ID: "ebay_payment_policy_id",
  RETURN_POLICY_ID: "ebay_return_policy_id",
  CURRENCY: "ebay_currency",
  RU_NAME: "ebay_ru_name",
};

/** OAuth scopes required to create and publish listings. */
export const REQUIRED_SCOPES = [
  "https://api.ebay.com/oauth/api_scope/sell.inventory",
  "https://api.ebay.com/oauth/api_scope/sell.account",
  // Order sync (server-side cron) reads sold orders. Consent granted before
  // this scope was added must be redone for order sync to work.
  "https://api.ebay.com/oauth/api_scope/sell.fulfillment",
].join(" ");

/** Refresh a little early so a token can't expire mid-flow. */
const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000;

/** App auth header for the worker proxy, matching services/api.js. */
function proxyHeaders() {
  const token = localStorage.getItem("access_token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * True when a response is the worker's own auth rejection (missing or expired
 * app JWT), as opposed to an eBay API error proxied through. The worker's auth
 * middleware answers 401 with {error: "No token provided"} when the
 * Authorization header is absent and {error: "Invalid or expired token"} when
 * the JWT fails verification.
 */
function isWorkerAuthFailure(status, data) {
  return (
    status === 401 &&
    (data?.error === "Invalid or expired token" ||
      data?.error === "No token provided")
  );
}

/**
 * POST to the worker with an app-session refresh-and-retry on auth failure.
 *
 * These raw fetch() calls bypass the axios 401-refresh interceptor in
 * services/api.js, so after the app JWT expires the worker 401s and the
 * failure used to be misread as broken eBay credentials. Refresh the app
 * session once, the same way the axios interceptor does, then retry the
 * original request once.
 *
 * @returns {Promise<{response, data}>} The (possibly retried) response with
 *   its parsed JSON body.
 */
async function proxyFetch(url, payload, isRetry = false) {
  const response = await fetch(url, {
    method: "POST",
    headers: proxyHeaders(),
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!isRetry && isWorkerAuthFailure(response.status, data)) {
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken) {
      try {
        const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
        const tokens = await refreshRes.json().catch(() => ({}));
        if (refreshRes.ok && tokens.accessToken) {
          localStorage.setItem("access_token", tokens.accessToken);
          if (tokens.refreshToken) {
            localStorage.setItem("refresh_token", tokens.refreshToken);
          }
          return proxyFetch(url, payload, true);
        }
      } catch (e) {
        // Refresh unreachable — surface the original 401 below.
      }
    }
  }

  return { response, data };
}

export function getCredentials() {
  return {
    clientId: localStorage.getItem(STORAGE_KEYS.CLIENT_ID),
    clientSecret: localStorage.getItem(STORAGE_KEYS.CLIENT_SECRET),
    refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
    accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
    expiresAt: Number(localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT)) || 0,
    environment: localStorage.getItem(STORAGE_KEYS.ENVIRONMENT) || "production",
    marketplaceId:
      localStorage.getItem(STORAGE_KEYS.MARKETPLACE_ID) || "EBAY_US",
    merchantLocationKey: localStorage.getItem(
      STORAGE_KEYS.MERCHANT_LOCATION_KEY,
    ),
    fulfillmentPolicyId: localStorage.getItem(
      STORAGE_KEYS.FULFILLMENT_POLICY_ID,
    ),
    paymentPolicyId: localStorage.getItem(STORAGE_KEYS.PAYMENT_POLICY_ID),
    returnPolicyId: localStorage.getItem(STORAGE_KEYS.RETURN_POLICY_ID),
    currency: localStorage.getItem(STORAGE_KEYS.CURRENCY) || "USD",
    ruName: localStorage.getItem(STORAGE_KEYS.RU_NAME),
  };
}

export function saveCredentials(creds = {}) {
  const map = {
    clientId: STORAGE_KEYS.CLIENT_ID,
    clientSecret: STORAGE_KEYS.CLIENT_SECRET,
    refreshToken: STORAGE_KEYS.REFRESH_TOKEN,
    environment: STORAGE_KEYS.ENVIRONMENT,
    marketplaceId: STORAGE_KEYS.MARKETPLACE_ID,
    merchantLocationKey: STORAGE_KEYS.MERCHANT_LOCATION_KEY,
    fulfillmentPolicyId: STORAGE_KEYS.FULFILLMENT_POLICY_ID,
    paymentPolicyId: STORAGE_KEYS.PAYMENT_POLICY_ID,
    returnPolicyId: STORAGE_KEYS.RETURN_POLICY_ID,
    currency: STORAGE_KEYS.CURRENCY,
    ruName: STORAGE_KEYS.RU_NAME,
  };

  Object.entries(map).forEach(([field, key]) => {
    if (creds[field] !== undefined && creds[field] !== null) {
      localStorage.setItem(key, creds[field]);
    }
  });

  // A new refresh token (or environment switch) invalidates any cached
  // access token.
  if (
    creds.refreshToken ||
    creds.clientId ||
    creds.clientSecret ||
    creds.environment
  ) {
    clearCachedToken();
  }

  // The app token and category tree id are minted for a specific
  // environment/marketplace; switching either (or the keys) must drop them
  // or Taxonomy calls keep hitting the old environment until a hard refresh.
  if (
    creds.clientId ||
    creds.clientSecret ||
    creds.environment ||
    creds.marketplaceId
  ) {
    resetTaxonomyCaches();
  }

  scheduleServerSync();
}

let serverSyncTimer = null;

/**
 * Mirror the credentials/config up to the server (D1) so the phone app and
 * the order-sync cron can act without localStorage. Debounced because several
 * saves land together during setup; failures are non-fatal — the web client
 * keeps working from localStorage either way.
 */
export function scheduleServerSync() {
  clearTimeout(serverSyncTimer);
  serverSyncTimer = setTimeout(() => {
    syncCredentialsToServer().catch((err) =>
      console.warn("eBay server credential sync failed:", err.message),
    );
  }, 1500);
}

export async function syncCredentialsToServer() {
  const creds = getCredentials();
  if (!creds.clientId) return; // nothing worth syncing yet

  let presentation;
  try {
    presentation =
      JSON.parse(localStorage.getItem("ebay_description_presentation")) ||
      undefined;
  } catch (err) {
    presentation = undefined;
  }

  await ebayAccount.syncCredentials({
    clientId: creds.clientId,
    clientSecret: creds.clientSecret,
    refreshToken: creds.refreshToken,
    environment: creds.environment,
    marketplaceId: creds.marketplaceId,
    merchantLocationKey: creds.merchantLocationKey,
    fulfillmentPolicyId: creds.fulfillmentPolicyId,
    paymentPolicyId: creds.paymentPolicyId,
    returnPolicyId: creds.returnPolicyId,
    currency: creds.currency,
    ruName: creds.ruName,
    // Template overrides ride along so server-rendered (phone) listings match.
    descriptionTemplate:
      localStorage.getItem("ebay_description_template") || undefined,
    presentation,
  });
}

export function clearCachedToken() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
}

export function clearCredentials() {
  Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  resetTaxonomyCaches();
}

/** True when we can obtain a token. Listing also needs business policies. */
export function isConfigured() {
  const creds = getCredentials();
  return !!(creds.clientId && creds.clientSecret && creds.refreshToken);
}

/** True when everything required to publish an offer is present. */
export function isReadyToList() {
  const creds = getCredentials();
  return !!(
    isConfigured() &&
    creds.fulfillmentPolicyId &&
    creds.paymentPolicyId &&
    creds.returnPolicyId &&
    creds.merchantLocationKey
  );
}

/** What still needs filling in before a listing can be published. */
export function getMissingRequirements() {
  const creds = getCredentials();
  const missing = [];
  if (!creds.clientId) missing.push("Client ID");
  if (!creds.clientSecret) missing.push("Client Secret");
  if (!creds.refreshToken) missing.push("Refresh Token");
  if (!creds.fulfillmentPolicyId) missing.push("Shipping policy");
  if (!creds.paymentPolicyId) missing.push("Payment policy");
  if (!creds.returnPolicyId) missing.push("Return policy");
  if (!creds.merchantLocationKey) missing.push("Inventory location");
  return missing;
}

/**
 * Read the environment out of an eBay App ID.
 *
 * App IDs are `devName-appName-ENV-hash-hash`, where ENV is PRD or SBX. The
 * app *name* is free text and can say anything — including "Sandbox" on a
 * production keyset — so only the ENV segment is trustworthy.
 *
 * @returns {"production"|"sandbox"|null} null when the format isn't recognised
 */
export function detectEnvironment(clientId) {
  const match = /-(PRD|SBX)-/.exec(clientId || "");
  if (!match) return null;
  return match[1] === "PRD" ? "production" : "sandbox";
}

/**
 * Warn when the selected environment disagrees with the App ID's ENV segment.
 *
 * Getting this wrong produces an opaque invalid_client error, so it's worth
 * catching before the request goes out.
 *
 * @returns {string|null} Warning text, or null when consistent
 */
export function getEnvironmentMismatch(credentials = getCredentials()) {
  const detected = detectEnvironment(credentials.clientId);
  if (!detected || detected === credentials.environment) return null;

  return `This App ID is a ${detected === "production" ? "production (PRD)" : "sandbox (SBX)"} keyset, but Environment is set to ${credentials.environment}. Authentication will fail.`;
}

/** Post to the proxy's token endpoint and return the parsed payload. */
async function requestToken(payload) {
  const { response, data } = await proxyFetch(`${PROXY_URL}/token`, payload);

  if (!response.ok) {
    // An app-session failure (refresh didn't help) says nothing about the
    // eBay credentials — let callers avoid clearing valid eBay state.
    const isWorkerAuth = isWorkerAuthFailure(response.status, data);
    const err = new Error(
      isWorkerAuth
        ? "Your session has expired — please sign in again"
        : data.details ||
            data.error ||
            `eBay token request failed (${response.status})`,
    );
    err.isWorkerAuthError = isWorkerAuth;
    throw err;
  }

  return data;
}

/**
 * Verify the app keys without any user consent.
 *
 * A client-credentials grant only proves the client id/secret pair is valid —
 * it can't list anything, so this is a setup check, not a listing path.
 */
export async function testCredentials(overrides = {}) {
  const creds = { ...getCredentials(), ...overrides };

  if (!creds.clientId || !creds.clientSecret) {
    throw new Error("Client ID and Client Secret are required");
  }

  await requestToken({
    grant: "client_credentials",
    clientId: creds.clientId,
    clientSecret: creds.clientSecret,
    environment: creds.environment,
  });

  return true;
}

/**
 * Build the eBay consent URL the seller opens to authorise this app.
 *
 * `redirect_uri` takes the RuName from the developer account, not the literal
 * callback URL.
 */
export function getConsentUrl(overrides = {}) {
  const creds = { ...getCredentials(), ...overrides };

  if (!creds.clientId || !creds.ruName) {
    throw new Error(
      "Client ID and RuName are required to build the consent URL",
    );
  }

  const host =
    creds.environment === "sandbox"
      ? "https://auth.sandbox.ebay.com"
      : "https://auth.ebay.com";

  const params = new URLSearchParams({
    client_id: creds.clientId,
    response_type: "code",
    redirect_uri: creds.ruName,
    scope: REQUIRED_SCOPES,
  });

  return `${host}/oauth2/authorize?${params.toString()}`;
}

/**
 * Pull the authorization code out of whatever the seller pastes back.
 *
 * Accepts the full redirect URL or a bare code. eBay URL-encodes the code in
 * the redirect, so it needs decoding before the exchange.
 */
export function extractAuthCode(input) {
  const trimmed = (input || "").trim();
  if (!trimmed) return "";

  const match = trimmed.match(/[?&]code=([^&\s]+)/);
  const raw = match ? match[1] : trimmed;

  try {
    return decodeURIComponent(raw);
  } catch (err) {
    return raw;
  }
}

/**
 * Exchange the consent code for a refresh token and store it.
 *
 * @param {string} codeOrUrl - The code, or the full redirect URL containing it
 * @returns {Promise<{refreshToken, expiresIn}>}
 */
export async function exchangeAuthCode(codeOrUrl, overrides = {}) {
  const creds = { ...getCredentials(), ...overrides };
  const code = extractAuthCode(codeOrUrl);

  if (!code) throw new Error("No authorization code found in that input");
  if (!creds.ruName) throw new Error("RuName is required for the exchange");

  const data = await requestToken({
    grant: "authorization_code",
    clientId: creds.clientId,
    clientSecret: creds.clientSecret,
    code,
    redirectUri: creds.ruName,
    environment: creds.environment,
  });

  if (!data.refresh_token) {
    throw new Error("eBay returned no refresh token for that code");
  }

  saveCredentials({ refreshToken: data.refresh_token });

  // The exchange also yields a usable access token — cache it so the next call
  // doesn't need an immediate refresh.
  if (data.access_token) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
    localStorage.setItem(
      STORAGE_KEYS.TOKEN_EXPIRES_AT,
      String(Date.now() + (data.expires_in || 7200) * 1000),
    );
  }

  return {
    refreshToken: data.refresh_token,
    expiresIn: data.refresh_token_expires_in,
  };
}

/**
 * Return a valid access token, refreshing through the proxy when the cached
 * one is missing or about to expire.
 */
export async function getAccessToken({ force = false } = {}) {
  const creds = getCredentials();

  if (!creds.clientId || !creds.clientSecret || !creds.refreshToken) {
    throw new Error("eBay credentials not configured");
  }

  if (
    !force &&
    creds.accessToken &&
    creds.expiresAt > Date.now() + TOKEN_EXPIRY_BUFFER_MS
  ) {
    return creds.accessToken;
  }

  let data;
  try {
    data = await requestToken({
      grant: "refresh_token",
      clientId: creds.clientId,
      clientSecret: creds.clientSecret,
      refreshToken: creds.refreshToken,
      scopes: REQUIRED_SCOPES,
      environment: creds.environment,
    });
  } catch (err) {
    // Only clear on genuine eBay credential failures — a worker-auth 401
    // (expired app session) says nothing about the cached eBay token.
    if (!err.isWorkerAuthError) clearCachedToken();
    throw err;
  }

  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
  localStorage.setItem(
    STORAGE_KEYS.TOKEN_EXPIRES_AT,
    String(Date.now() + (data.expires_in || 7200) * 1000),
  );

  return data.access_token;
}

/**
 * Call an eBay REST endpoint through the proxy.
 *
 * Retries once on 401 with a freshly minted token, since eBay can invalidate a
 * token before its stated expiry.
 */
async function request(method, path, body, options = {}) {
  const creds = getCredentials();
  const accessToken = await getAccessToken({ force: options.forceToken });

  const { response, data } = await proxyFetch(`${PROXY_URL}/proxy`, {
    method,
    path,
    body,
    accessToken,
    environment: creds.environment,
    marketplaceId: creds.marketplaceId,
    headers: options.headers,
  });

  if (!response.ok) {
    // A worker-auth 401 that survived proxyFetch's refresh means the app
    // session is gone — an eBay token retry can't fix it.
    if (
      response.status === 401 &&
      !options.forceToken &&
      !isWorkerAuthFailure(response.status, data)
    ) {
      return request(method, path, body, { ...options, forceToken: true });
    }

    // eBay's Inventory Service returns transient 5xx ("Core Inventory Service
    // internal error") under load, especially in sandbox. One retry clears it
    // often enough to be worth the wait.
    if (response.status >= 500 && !options.retried) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return request(method, path, body, { ...options, retried: true });
    }

    const err = new Error(formatError(data, response.status));
    err.status = response.status;
    throw err;
  }

  return data;
}

/** Turn an eBay error payload into a message worth showing a user. */
function formatError(data, status) {
  let details = data?.details;

  if (typeof details === "string") {
    try {
      details = JSON.parse(details);
    } catch (e) {
      return `eBay error ${status}: ${details}`;
    }
  }

  const errors = details?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors
      .map((err) => {
        const params = (err.parameters || [])
          .map((p) => `${p.name}=${p.value}`)
          .join(", ");
        return [err.message, err.longMessage, params]
          .filter(Boolean)
          .join(" — ");
      })
      .join("; ");
  }

  return data?.error || `eBay request failed (${status})`;
}

// ---------------------------------------------------------------------------
// Account configuration lookups
// ---------------------------------------------------------------------------

/** Fetch the seller's shipping, payment and return business policies. */
export async function getPolicies() {
  const { marketplaceId } = getCredentials();
  const query = `?marketplace_id=${encodeURIComponent(marketplaceId)}`;

  const [fulfillment, payment, returns] = await Promise.all([
    request("GET", `/sell/account/v1/fulfillment_policy${query}`).catch(
      () => null,
    ),
    request("GET", `/sell/account/v1/payment_policy${query}`).catch(() => null),
    request("GET", `/sell/account/v1/return_policy${query}`).catch(() => null),
  ]);

  return {
    fulfillment: fulfillment?.fulfillmentPolicies || [],
    payment: payment?.paymentPolicies || [],
    returns: returns?.returnPolicies || [],
  };
}

/**
 * Programs the seller account is enrolled in.
 *
 * Business policies can only be created once the account is opted into
 * SELLING_POLICY_MANAGEMENT, which many accounts — sandbox ones especially —
 * are not by default.
 */
export async function getOptedInPrograms() {
  const data = await request(
    "GET",
    "/sell/account/v1/program/get_opted_in_programs",
  );
  return (data?.programs || []).map((p) => p.programType);
}

/**
 * Opt the account into business policy management. Idempotent.
 *
 * Checks enrolment first, because eBay reports an already-enrolled account as
 * an error ("programType already exists") rather than a no-op.
 */
export async function optInToBusinessPolicies() {
  try {
    const programs = await getOptedInPrograms();
    if (programs.includes("SELLING_POLICY_MANAGEMENT")) return true;
  } catch (err) {
    // Couldn't read enrolment — fall through and just try opting in.
  }

  try {
    await request("POST", "/sell/account/v1/program/opt_in", {
      programType: "SELLING_POLICY_MANAGEMENT",
    });
    return true;
  } catch (err) {
    // Every phrasing eBay uses for "you are already enrolled".
    if (/already (opted.?in|enrolled|exists)/i.test(err.message)) return true;
    throw err;
  }
}

const ALL_CATEGORIES = [{ name: "ALL_EXCLUDING_MOTORS_VEHICLES" }];

/**
 * Shipping service codes to try, most preferred first.
 *
 * eBay's accepted codes differ between marketplaces and between sandbox and
 * production, and they change over time — USPSGroundAdvantage is rejected in
 * sandbox with UNKNOWN_SHIPPING_SERVICE_CODE, for instance. Rather than pin
 * one code, try them in order and keep the first eBay accepts.
 */
const SHIPPING_SERVICE_CANDIDATES = [
  { code: "USPSGroundAdvantage", carrier: "USPS" },
  { code: "USPSPriority", carrier: "USPS" },
  { code: "USPSFirstClass", carrier: "USPS" },
  { code: "USPSParcel", carrier: "USPS" },
  { code: "USPSMedia", carrier: "USPS" },
  { code: "ShippingMethodStandard", carrier: null },
  { code: "Other", carrier: null },
];

/**
 * Ask eBay which shipping services this marketplace actually accepts.
 *
 * Returns [] when the metadata endpoint is unavailable, in which case the
 * caller falls back to trying the candidate list.
 */
export async function getShippingServiceCodes() {
  const { marketplaceId } = getCredentials();
  try {
    const data = await request(
      "GET",
      `/sell/metadata/v1/marketplace/${encodeURIComponent(marketplaceId)}/get_shipping_services`,
    );
    return (data?.shippingServices || [])
      .filter((svc) => svc.shippingCarrierCode && svc.shippingServiceCode)
      .map((svc) => ({
        code: svc.shippingServiceCode,
        carrier: svc.shippingCarrierCode,
      }));
  } catch (err) {
    return [];
  }
}

/** Build a fulfillment policy body for one shipping service. */
function fulfillmentPolicyBody(
  service,
  { marketplaceId, currency, handlingDays, shippingCost },
) {
  return {
    name: "Standard Shipping",
    marketplaceId,
    categoryTypes: ALL_CATEGORIES,
    handlingTime: { value: handlingDays, unit: "DAY" },
    shippingOptions: [
      {
        optionType: "DOMESTIC",
        costType: "FLAT_RATE",
        shippingServices: [
          {
            sortOrder: 1,
            ...(service.carrier && { shippingCarrierCode: service.carrier }),
            shippingServiceCode: service.code,
            shippingCost: { value: shippingCost, currency },
            additionalShippingCost: { value: "0.00", currency },
            freeShipping: false,
            buyerResponsibleForShipping: false,
          },
        ],
      },
    ],
  };
}

/**
 * Create a baseline shipping, payment and return policy.
 *
 * Deliberately plain defaults — the point is to get listing unblocked. Edit
 * them in Seller Hub afterwards; the IDs stay the same. Existing policies are
 * left alone rather than duplicated.
 *
 * @returns {Promise<{created: string[], skipped: string[], errors: string[]}>}
 */
export async function createDefaultPolicies(options = {}) {
  const { shippingCost = "5.00", handlingDays = 1, returnDays = 30 } = options;
  const { marketplaceId, currency } = getCredentials();

  // Non-fatal: if enrolment is genuinely missing, the policy calls below fail
  // with a clearer message than anything we could surface from here.
  try {
    await optInToBusinessPolicies();
  } catch (err) {
    console.log("Business policy opt-in skipped:", err.message);
  }

  const existing = await getPolicies();
  const created = [];
  const skipped = [];
  const errors = [];

  const ensure = async (label, alreadyThere, path, body, idField) => {
    if (alreadyThere.length > 0) {
      skipped.push(label);
      return alreadyThere[0][idField];
    }
    try {
      const data = await request("POST", `/sell/account/v1/${path}`, body);
      created.push(label);
      return data?.[idField];
    } catch (err) {
      errors.push(`${label}: ${err.message}`);
      return null;
    }
  };

  // Shipping needs a service code eBay recognises here; discover or probe.
  let fulfillmentPolicyId = null;
  if (existing.fulfillment.length > 0) {
    fulfillmentPolicyId = existing.fulfillment[0].fulfillmentPolicyId;
    skipped.push("shipping");
  } else {
    const discovered = await getShippingServiceCodes();
    const candidates = [
      // Prefer codes eBay itself reports, ordered by our preference.
      ...SHIPPING_SERVICE_CANDIDATES.filter((c) =>
        discovered.some((d) => d.code === c.code),
      ),
      ...SHIPPING_SERVICE_CANDIDATES,
    ].filter(
      (candidate, index, all) =>
        all.findIndex((c) => c.code === candidate.code) === index,
    );

    const attempts = [];
    for (const service of candidates) {
      try {
        const data = await request(
          "POST",
          "/sell/account/v1/fulfillment_policy",
          fulfillmentPolicyBody(service, {
            marketplaceId,
            currency,
            handlingDays,
            shippingCost,
          }),
        );
        fulfillmentPolicyId = data?.fulfillmentPolicyId;
        created.push(`shipping (${service.code})`);
        break;
      } catch (err) {
        attempts.push(service.code);
      }
    }

    if (!fulfillmentPolicyId) {
      errors.push(
        `shipping: eBay rejected every shipping service tried (${attempts.join(", ")}). Create a shipping policy manually in Seller Hub.`,
      );
    }
  }

  const paymentPolicyId = await ensure(
    "payment",
    existing.payment,
    "payment_policy",
    {
      name: "Standard Payment",
      marketplaceId,
      categoryTypes: ALL_CATEGORIES,
      immediatePay: true,
    },
    "paymentPolicyId",
  );

  const returnPolicyId = await ensure(
    "return",
    existing.returns,
    "return_policy",
    {
      name: `${returnDays} Day Returns`,
      marketplaceId,
      categoryTypes: ALL_CATEGORIES,
      returnsAccepted: true,
      returnPeriod: { value: returnDays, unit: "DAY" },
      returnShippingCostPayer: "BUYER",
      returnMethod: "MONEY_BACK",
    },
    "returnPolicyId",
  );

  // Select whatever we ended up with so the user doesn't have to.
  saveCredentials({
    ...(fulfillmentPolicyId && { fulfillmentPolicyId }),
    ...(paymentPolicyId && { paymentPolicyId }),
    ...(returnPolicyId && { returnPolicyId }),
  });

  return { created, skipped, errors };
}

/**
 * Turn free shipping on or off for a fulfillment policy.
 *
 * Free shipping is a property of the shipping policy, not the listing, so this
 * affects every listing using that policy. Reads the policy first so the rest
 * of its configuration survives the update.
 */
export async function setFreeShipping(policyId, enabled) {
  const { marketplaceId, currency } = getCredentials();

  const policy = await request(
    "GET",
    `/sell/account/v1/fulfillment_policy/${encodeURIComponent(policyId)}`,
  );

  const shippingOptions = (policy.shippingOptions || []).map((option) => {
    if (option.optionType !== "DOMESTIC") return option;
    return {
      ...option,
      shippingServices: (option.shippingServices || []).map((svc, index) =>
        index === 0
          ? {
              ...svc,
              freeShipping: enabled,
              // eBay requires the cost to be zero when freeShipping is set.
              shippingCost: {
                value: enabled ? "0.00" : svc.shippingCost?.value || "5.00",
                currency,
              },
            }
          : svc,
      ),
    };
  });

  await request(
    "PUT",
    `/sell/account/v1/fulfillment_policy/${encodeURIComponent(policyId)}`,
    {
      name: policy.name,
      marketplaceId: policy.marketplaceId || marketplaceId,
      categoryTypes: policy.categoryTypes,
      handlingTime: policy.handlingTime,
      shippingOptions,
      ...(policy.description && { description: policy.description }),
      ...(policy.globalShipping !== undefined && {
        globalShipping: policy.globalShipping,
      }),
    },
  );

  return true;
}

/** Whether a fulfillment policy currently offers free domestic shipping. */
export async function isFreeShipping(policyId) {
  const policy = await request(
    "GET",
    `/sell/account/v1/fulfillment_policy/${encodeURIComponent(policyId)}`,
  );
  return (policy.shippingOptions || []).some(
    (o) =>
      o.optionType === "DOMESTIC" &&
      (o.shippingServices || []).some((svc) => svc.freeShipping),
  );
}

/** List the seller's inventory locations. */
export async function getLocations() {
  const data = await request("GET", "/sell/inventory/v1/location?limit=100");
  return data?.locations || [];
}

/**
 * Create an inventory location. eBay requires one before an offer can publish.
 *
 * @param {string} key - Merchant-defined location key
 * @param {Object} address - { addressLine1, city, stateOrProvince, postalCode, country }
 */
export async function createLocation(key, address, name = "Default Location") {
  await request(
    "POST",
    `/sell/inventory/v1/location/${encodeURIComponent(key)}`,
    {
      location: { address },
      name,
      merchantLocationStatus: "ENABLED",
      locationTypes: ["WAREHOUSE"],
    },
  );
  return key;
}

// ---------------------------------------------------------------------------
// Category lookup
// ---------------------------------------------------------------------------

/**
 * Application-token cache.
 *
 * The Taxonomy API needs eBay's base api_scope, which the user's refresh token
 * doesn't carry (it was consented for sell.inventory + sell.account only).
 * Taxonomy data isn't user-specific, so a client-credentials token is both
 * sufficient and avoids forcing the seller through consent again.
 */
let appTokenCache = { token: null, expiresAt: 0 };

async function getAppAccessToken() {
  if (appTokenCache.token && appTokenCache.expiresAt > Date.now() + 60_000) {
    return appTokenCache.token;
  }

  const creds = getCredentials();
  const data = await requestToken({
    grant: "client_credentials",
    clientId: creds.clientId,
    clientSecret: creds.clientSecret,
    environment: creds.environment,
  });

  appTokenCache = {
    token: data.access_token,
    expiresAt: Date.now() + (data.expires_in || 7200) * 1000,
  };
  return appTokenCache.token;
}

/**
 * GET through the proxy using the application token.
 *
 * Falls back to the user token if the app token can't be minted, since some
 * accounts do carry api_scope on the user token.
 */
async function appRequest(path) {
  const creds = getCredentials();

  let accessToken;
  try {
    accessToken = await getAppAccessToken();
  } catch (err) {
    return request("GET", path);
  }

  const { response, data } = await proxyFetch(`${PROXY_URL}/proxy`, {
    method: "GET",
    path,
    accessToken,
    environment: creds.environment,
    marketplaceId: creds.marketplaceId,
  });

  if (!response.ok) throw new Error(formatError(data, response.status));
  return data;
}

let cachedCategoryTreeId = null;

/** Drop session caches minted for a specific environment/marketplace. */
function resetTaxonomyCaches() {
  appTokenCache = { token: null, expiresAt: 0 };
  cachedCategoryTreeId = null;
}

/** The category tree id for the configured marketplace (cached per session). */
export async function getCategoryTreeId() {
  if (cachedCategoryTreeId) return cachedCategoryTreeId;

  const { marketplaceId } = getCredentials();
  const data = await appRequest(
    `/commerce/taxonomy/v1/get_default_category_tree_id?marketplace_id=${encodeURIComponent(marketplaceId)}`,
  );

  cachedCategoryTreeId = data?.categoryTreeId;
  return cachedCategoryTreeId;
}

/**
 * Suggest leaf categories for a title.
 *
 * @returns {Array<{categoryId, categoryName, categoryPath}>}
 */
export async function getCategorySuggestions(title) {
  const treeId = await getCategoryTreeId();
  if (!treeId) return [];

  const data = await appRequest(
    `/commerce/taxonomy/v1/category_tree/${treeId}/get_category_suggestions?q=${encodeURIComponent(title)}`,
  );

  return (data?.categorySuggestions || []).map((suggestion) => ({
    categoryId: suggestion.category?.categoryId,
    categoryName: suggestion.category?.categoryName,
    categoryPath: (suggestion.categoryTreeNodeAncestors || [])
      .map((node) => node.categoryName)
      .reverse()
      .concat(suggestion.category?.categoryName)
      .filter(Boolean)
      .join(" > "),
  }));
}

/**
 * Required item aspects for a category (e.g. Brand, Type, Size).
 *
 * Publishing fails with "The item specific X is missing" for any of these
 * that the inventory item's aspects don't cover.
 *
 * @returns {Array<{name, mode, values}>} mode is FREE_TEXT or SELECTION_ONLY;
 *   values are eBay's suggested/allowed values for the aspect.
 */
export async function getRequiredAspects(categoryId) {
  const treeId = await getCategoryTreeId();
  if (!treeId) return [];

  const data = await appRequest(
    `/commerce/taxonomy/v1/category_tree/${treeId}/get_item_aspects_for_category?category_id=${encodeURIComponent(categoryId)}`,
  );

  return (data?.aspects || [])
    .filter((aspect) => aspect.aspectConstraint?.aspectRequired)
    .map((aspect) => ({
      name: aspect.localizedAspectName,
      mode: aspect.aspectConstraint?.aspectMode || "FREE_TEXT",
      values: (aspect.aspectValues || []).map((v) => v.localizedValue),
    }));
}

/** The single best category for a title, or null when eBay has no suggestion. */
export async function getAutoCategory(title) {
  const suggestions = await getCategorySuggestions(title);
  return suggestions[0] || null;
}

// ---------------------------------------------------------------------------
// Inventory items and offers
// ---------------------------------------------------------------------------

/** eBay caps the inventory item's product.description at 4000 characters. */
export const ITEM_DESCRIPTION_MAX = 4000;

/**
 * Reduce listing HTML to plain text for the inventory item description.
 *
 * The rich HTML belongs on the offer's listingDescription, which allows far
 * more. The inventory item wants a short summary, so strip markup (styles and
 * scripts entirely, not just their tags) and truncate at a word boundary.
 */
export function toItemDescription(html, maxLength = ITEM_DESCRIPTION_MAX) {
  const text = String(html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&times;/gi, "x")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= maxLength) return text;

  const cut = text.slice(0, maxLength - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > maxLength * 0.8 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * Map an internal condition to an eBay Inventory API condition enum.
 * See: https://developer.ebay.com/api-docs/sell/inventory/types/slr:ConditionEnum
 */
export function mapCondition(condition) {
  const mapping = {
    new: "NEW",
    like_new: "LIKE_NEW",
    good: "USED_GOOD",
    fair: "USED_ACCEPTABLE",
    poor: "FOR_PARTS_OR_NOT_WORKING",
  };
  return mapping[condition] || "USED_GOOD";
}

/**
 * Build the eBay SKU for a product.
 *
 * Keyed on the internal entity id so two units of the same Amazon item can't
 * collide on a shared ASIN.
 */
export function buildSku(product) {
  return `FS-${product.id}`;
}

/** Create or replace the inventory item for a SKU. */
export async function createOrReplaceInventoryItem(sku, item) {
  const {
    title,
    description,
    imageUrls = [],
    condition,
    conditionDescription,
    quantity = 1,
    brand,
    mpn,
    aspects,
    weightLbs,
    weightOz,
  } = item;

  // Fulfillment policies with calculated shipping reject a publish when the
  // inventory item carries no package weight (eBay error 216121).
  const totalOunces =
    (parseFloat(weightLbs) || 0) * 16 + (parseFloat(weightOz) || 0);

  // eBay validates required item specifics (Brand, MPN) against aspects, not
  // the top-level brand/mpn fields — mirror them in so publish doesn't fail
  // with "The item specific Brand is missing".
  const mergedAspects = {
    ...(brand && { Brand: [brand] }),
    ...(mpn && { MPN: [mpn] }),
    ...aspects,
  };

  const product = {
    title,
    // Clamp defensively — callers can reach this directly.
    description: toItemDescription(description) || title,
    ...(imageUrls.length > 0 && { imageUrls }),
    ...(brand && { brand }),
    ...(mpn && { mpn }),
    ...(Object.keys(mergedAspects).length > 0 && { aspects: mergedAspects }),
  };

  return request(
    "PUT",
    `/sell/inventory/v1/inventory_item/${encodeURIComponent(sku)}`,
    {
      availability: { shipToLocationAvailability: { quantity } },
      condition,
      ...(conditionDescription && { conditionDescription }),
      ...(totalOunces > 0 && {
        packageWeightAndSize: {
          weight: {
            value: Math.round(totalOunces * 100) / 100,
            unit: "OUNCE",
          },
        },
      }),
      product,
    },
  );
}

/** Find an existing offer for a SKU on the configured marketplace. */
export async function getOfferBySku(sku) {
  const { marketplaceId } = getCredentials();
  try {
    const data = await request(
      "GET",
      `/sell/inventory/v1/offer?sku=${encodeURIComponent(sku)}&marketplace_id=${encodeURIComponent(marketplaceId)}`,
    );
    return data?.offers?.[0] || null;
  } catch (err) {
    // eBay returns 404 when the SKU has no offers yet. Anything else (5xx,
    // auth) must surface — swallowing it would send postProduct down the
    // create-offer path and produce a misleading duplicate-offer error.
    if (err.status === 404) return null;
    throw err;
  }
}

function buildOfferPayload(
  sku,
  {
    categoryId,
    description,
    price,
    quantity = 1,
    bestOffer = false,
    autoAcceptPrice,
    autoDeclinePrice,
  },
) {
  const creds = getCredentials();

  const listingPolicies = {
    fulfillmentPolicyId: creds.fulfillmentPolicyId,
    paymentPolicyId: creds.paymentPolicyId,
    returnPolicyId: creds.returnPolicyId,
  };

  // Best Offer is per-listing, unlike shipping/payment/returns which come from
  // account-level policies.
  if (bestOffer) {
    listingPolicies.bestOfferTerms = {
      bestOfferEnabled: true,
      ...(autoAcceptPrice && {
        autoAcceptPrice: {
          value: String(autoAcceptPrice),
          currency: creds.currency,
        },
      }),
      ...(autoDeclinePrice && {
        autoDeclinePrice: {
          value: String(autoDeclinePrice),
          currency: creds.currency,
        },
      }),
    };
  }

  return {
    sku,
    marketplaceId: creds.marketplaceId,
    format: "FIXED_PRICE",
    availableQuantity: quantity,
    categoryId,
    listingDescription: description,
    listingPolicies,
    pricingSummary: {
      price: { value: String(price), currency: creds.currency },
    },
    merchantLocationKey: creds.merchantLocationKey,
  };
}

export async function createOffer(sku, offer) {
  const data = await request(
    "POST",
    "/sell/inventory/v1/offer",
    buildOfferPayload(sku, offer),
  );
  return data?.offerId;
}

export async function updateOffer(offerId, sku, offer) {
  await request(
    "PUT",
    `/sell/inventory/v1/offer/${encodeURIComponent(offerId)}`,
    buildOfferPayload(sku, offer),
  );
  return offerId;
}

/** Publish an offer, producing the live eBay listing. */
export async function publishOffer(offerId) {
  const data = await request(
    "POST",
    `/sell/inventory/v1/offer/${encodeURIComponent(offerId)}/publish`,
  );
  return data?.listingId;
}

/** End a live listing but keep the offer for relisting. */
export async function withdrawOffer(offerId) {
  return request(
    "POST",
    `/sell/inventory/v1/offer/${encodeURIComponent(offerId)}/withdraw`,
  );
}

/** End the listing and delete the offer entirely. */
export async function deleteOffer(offerId) {
  try {
    await withdrawOffer(offerId);
  } catch (err) {
    // An unpublished offer can't be withdrawn — deleting it is still valid.
    console.log("Withdraw skipped:", err.message);
  }
  return request(
    "DELETE",
    `/sell/inventory/v1/offer/${encodeURIComponent(offerId)}`,
  );
}

/** Remove an inventory item and any offers attached to it. */
export async function deleteInventoryItem(sku) {
  return request(
    "DELETE",
    `/sell/inventory/v1/inventory_item/${encodeURIComponent(sku)}`,
  );
}

/** Public URL for a published listing. */
export function getListingUrl(listingId) {
  const { environment } = getCredentials();
  const host = environment === "sandbox" ? "sandbox.ebay.com" : "www.ebay.com";
  return `https://${host}/itm/${listingId}`;
}

/**
 * Full flow: publish a product to eBay.
 *
 * Creates (or replaces) the inventory item, attaches an offer — reusing an
 * existing offer for the SKU rather than failing on a duplicate — and
 * publishes it.
 *
 * @param {Object} product - Product entity
 * @param {Object} options - { title, price, description, categoryId, condition, images, quantity, brand, mpn, aspects, conditionDescription, weightLbs, weightOz }
 * @returns {Promise<{listingId, offerId, sku, listingUrl}>}
 */
export async function postProduct(product, options = {}) {
  const missing = getMissingRequirements();
  if (missing.length > 0) {
    throw new Error(`eBay setup incomplete — missing: ${missing.join(", ")}`);
  }

  const {
    title = product.name,
    price = product.list_sale_price || product.retail_price || 0,
    description = "",
    categoryId,
    condition: rawCondition = product.condition,
    images = [],
    quantity = 1,
    brand,
    mpn,
    aspects,
    conditionDescription,
    weightLbs,
    weightOz,
    bestOffer = false,
    autoAcceptPrice,
    autoDeclinePrice,
    // When false, the inventory item and offer are created but the offer is
    // not published — nothing becomes visible or buyable on eBay. Used to
    // rehearse the full write path against production safely.
    publish = true,
  } = options;

  if (!categoryId) {
    throw new Error("An eBay category is required to publish a listing");
  }
  if (publish && images.length === 0) {
    // eBay rejects a publish with no photos; fail here with a clear reason.
    throw new Error("At least one photo is required to publish on eBay");
  }

  const sku = buildSku(product);

  // eBay's errors don't say which call failed, so label each step. Without
  // this, "Core Inventory Service internal error" is unattributable.
  const step = async (label, fn) => {
    try {
      return await fn();
    } catch (err) {
      throw new Error(`[${label}] ${err.message}`);
    }
  };

  // The inventory item takes a short plain-text summary; the full HTML goes on
  // the offer below, where eBay allows the length.
  await step("create inventory item", () =>
    createOrReplaceInventoryItem(sku, {
      title,
      description: toItemDescription(description) || title,
      imageUrls: images,
      condition: mapCondition(rawCondition),
      conditionDescription,
      quantity,
      brand,
      mpn,
      aspects,
      weightLbs,
      weightOz,
    }),
  );

  const offerData = {
    categoryId,
    description,
    price,
    quantity,
    bestOffer,
    autoAcceptPrice,
    autoDeclinePrice,
  };

  const existingOffer = await step("look up existing offer", () =>
    getOfferBySku(sku),
  );
  const offerId = existingOffer?.offerId
    ? await step("update offer", () =>
        updateOffer(existingOffer.offerId, sku, offerData),
      )
    : await step("create offer", () => createOffer(sku, offerData));

  if (!publish) {
    return {
      listingId: null,
      offerId,
      sku,
      listingUrl: null,
      published: false,
    };
  }

  // A previously published offer keeps its listing id; publishing again just
  // pushes the revision live.
  const listingId = await step("publish offer", () => publishOffer(offerId));

  return {
    listingId,
    offerId,
    sku,
    listingUrl: getListingUrl(listingId),
    published: true,
  };
}

/**
 * Update an already-published listing.
 *
 * @param {Object} product - Product entity (used to derive the SKU)
 * @param {Object} options - Same shape as postProduct options
 */
export async function updateListing(product, options = {}) {
  return postProduct(product, options);
}

/**
 * End a listing on eBay.
 *
 * @param {string} offerId - The offer backing the listing
 */
export async function endListing(offerId) {
  return deleteOffer(offerId);
}

const ebayService = {
  REQUIRED_SCOPES,
  getCredentials,
  saveCredentials,
  scheduleServerSync,
  syncCredentialsToServer,
  clearCredentials,
  clearCachedToken,
  isConfigured,
  isReadyToList,
  getMissingRequirements,
  getAccessToken,
  detectEnvironment,
  getEnvironmentMismatch,
  testCredentials,
  getConsentUrl,
  extractAuthCode,
  exchangeAuthCode,
  getPolicies,
  getOptedInPrograms,
  getShippingServiceCodes,
  setFreeShipping,
  isFreeShipping,
  optInToBusinessPolicies,
  createDefaultPolicies,
  getLocations,
  createLocation,
  getCategoryTreeId,
  getCategorySuggestions,
  getRequiredAspects,
  getAutoCategory,
  mapCondition,
  toItemDescription,
  ITEM_DESCRIPTION_MAX,
  buildSku,
  createOrReplaceInventoryItem,
  getOfferBySku,
  createOffer,
  updateOffer,
  publishOffer,
  deleteInventoryItem,
  withdrawOffer,
  deleteOffer,
  getListingUrl,
  postProduct,
  updateListing,
  endListing,
};

export default ebayService;
